import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { platformWorkAPI } from '../../services/api';
import { EntityCustomFields } from '../shared/EntityCustomFields';
import { POKindTag } from '../shared/POKindTag';

const NONE = '__none__';

const emptyForm = {
  title: '', description: '', status: '', platform: '', ms_owner: '',
  assignee: '', target_date: '', project_id: '', linked_po_id: '', custom_fields: {},
};

export const PlatformFormDialog = ({
  open, onOpenChange, itemType, initial, stages, taxonomies, projects, platformPOs, currentUser, isSuperAdmin, onSchemaChanged, onSaved,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (open) {
      setForm(initial ? {
        title: initial.title || '',
        description: initial.description || '',
        status: initial.status || (stages[0]?.value || ''),
        platform: initial.platform || '',
        ms_owner: initial.ms_owner || '',
        assignee: initial.assignee || '',
        target_date: initial.target_date || '',
        project_id: initial.project_id || '',
        linked_po_id: initial.linked_po_id || '',
        custom_fields: initial.custom_fields || {},
      } : { ...emptyForm, status: stages[0]?.value || '' });
    }
  }, [open, initial, stages]);

  const platformItems = (taxonomies.platform || []).filter(i => i.active);
  const teamItems = (taxonomies.levelup_team || []).filter(i => i.active);
  const msOwnerItems = (taxonomies.ms_owner || []).filter(i => i.active);

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const po = platformPOs.find(p => p.id === form.linked_po_id);
      const payload = {
        item_type: itemType,
        title: form.title.trim(),
        description: form.description || null,
        status: form.status || null,
        platform: form.platform || null,
        ms_owner: form.ms_owner || null,
        custom_fields: form.custom_fields || {},
      };
      if (itemType === 'task') {
        payload.assignee = form.assignee || null;
        payload.target_date = form.target_date || null;
        payload.project_id = form.project_id || null;
      } else {
        payload.linked_po_id = form.linked_po_id || null;
        payload.linked_po_number = po?.po_number || null;
      }
      if (isEdit) {
        await platformWorkAPI.update(initial.id, payload);
        toast.success(`${itemType === 'project' ? 'Project' : 'Task'} updated`);
      } else {
        await platformWorkAPI.create(payload, currentUser?.email || currentUser?.name || null);
        toast.success(`${itemType === 'project' ? 'Project' : 'Task'} created`);
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const label = itemType === 'project' ? 'Project' : 'Task';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="platform-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${label}` : `New ${label}`}</DialogTitle>
          <DialogDescription>
            {itemType === 'project'
              ? 'A larger program of work on the LevelUp platform, optionally funded by a PO.'
              : 'A discrete piece of work assigned to a team member, optionally attached to a project.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder={itemType === 'project' ? 'e.g. Build FY27 Sales Journey on LevelUp' : 'e.g. Ingest MS AI-900 course into Cypher'}
              data-testid="platform-title-input" />
          </div>

          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Details, scope, notes..." rows={3} data-testid="platform-description-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Platform</Label>
              <Select value={form.platform || NONE} onValueChange={v => setForm({ ...form, platform: v === NONE ? '' : v })}>
                <SelectTrigger data-testid="platform-platform-select"><SelectValue placeholder="Select platform" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— None —</SelectItem>
                  {platformItems.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status || NONE} onValueChange={v => setForm({ ...form, status: v === NONE ? '' : v })}>
                <SelectTrigger data-testid="platform-status-select"><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  {stages.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>MS Owner</Label>
            <Select value={form.ms_owner || NONE} onValueChange={v => setForm({ ...form, ms_owner: v === NONE ? '' : v })}>
              <SelectTrigger data-testid="platform-msowner-select"><SelectValue placeholder="Select MS owner" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>— None —</SelectItem>
                {msOwnerItems.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {msOwnerItems.length === 0 && (
              <p className="text-xs text-muted-foreground mt-1">Add MS Owners in Taxonomy settings to populate this list.</p>
            )}
          </div>

          {itemType === 'task' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Assignee</Label>
                  <Select value={form.assignee || NONE} onValueChange={v => setForm({ ...form, assignee: v === NONE ? '' : v })}>
                    <SelectTrigger data-testid="platform-assignee-select"><SelectValue placeholder="Select member" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— Unassigned —</SelectItem>
                      {teamItems.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Target Date</Label>
                  <Input type="date" value={form.target_date} onChange={e => setForm({ ...form, target_date: e.target.value })} data-testid="platform-target-date-input" />
                </div>
              </div>
              {teamItems.length === 0 && (
                <p className="text-xs text-muted-foreground -mt-2">Add LevelUp Team Members in Taxonomy settings to assign tasks.</p>
              )}
              <div>
                <Label>Attach to Project (optional)</Label>
                <Select value={form.project_id || NONE} onValueChange={v => setForm({ ...form, project_id: v === NONE ? '' : v })}>
                  <SelectTrigger data-testid="platform-project-select"><SelectValue placeholder="Standalone task" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>— Standalone (no project) —</SelectItem>
                    {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {itemType === 'project' && (
            <div>
              <Label>Linked Purchase Order</Label>
              <Select value={form.linked_po_id || NONE} onValueChange={v => setForm({ ...form, linked_po_id: v === NONE ? '' : v })}>
                <SelectTrigger data-testid="platform-po-select"><SelectValue placeholder="Select PO" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— No PO —</SelectItem>
                  {platformPOs.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="flex items-center gap-2">
                        <span>{p.display_label || `${p.po_number}${p.short_title ? ` — ${p.short_title}` : ''}`}</span>
                        <POKindTag kind={p.kind} />
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {platformPOs.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">No purchase orders yet. Add one in Purchase Orders.</p>
              )}
            </div>
          )}

          <div className="pt-2 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground mb-3">Additional Taxonomy Fields</p>
            <EntityCustomFields
              entityType={itemType === 'project' ? 'platform_project' : 'platform_task'}
              values={form.custom_fields || {}}
              onChange={(cf) => setForm({ ...form, custom_fields: cf })}
              isSuperAdmin={isSuperAdmin}
              currentUser={currentUser}
              onSchemaChanged={onSchemaChanged}
              testPrefix="platform-custom-field"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="platform-save-btn" className="bg-lime text-lime-foreground hover:bg-lime/90">
            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : `Create ${label}`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PlatformFormDialog;
